import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cx } from './cx';

type ToastTone = 'info' | 'success' | 'warn';

export interface ToastPushOptions {
  onClick?: () => void;
}

interface Toast {
  id: number;
  tone: ToastTone;
  text: string;
  onClick?: () => void;
}

interface ToastApi {
  push(text: string, tone?: ToastTone, options?: ToastPushOptions): void;
}

const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback(
    (text: string, tone: ToastTone = 'info', options?: ToastPushOptions) => {
      const id = Date.now() + Math.random();
      setItems((prev) => [...prev, { id, tone, text, onClick: options?.onClick }]);
      setTimeout(() => {
        setItems((prev) => prev.filter((t) => t.id !== id));
      }, 4500);
    },
    [],
  );

  const api = useMemo(() => ({ push }), [push]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {items.map((t) => (
          <div
            key={t.id}
            role={t.onClick ? 'button' : undefined}
            tabIndex={t.onClick ? 0 : undefined}
            onClick={t.onClick}
            onKeyDown={
              t.onClick
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      t.onClick?.();
                    }
                  }
                : undefined
            }
            className={cx(
              'pointer-events-auto min-w-[16rem] max-w-sm px-4 py-3 text-sm border shadow-card',
              t.tone === 'success' && 'bg-linen border-olive/40 text-ink',
              t.tone === 'warn' && 'bg-linen border-clay/60 text-ink',
              t.tone === 'info' && 'bg-linen border-rule-strong text-ink',
              t.onClick && 'cursor-pointer hover:border-ink/30',
            )}
          >
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    // Fail soft so a module that forgets the provider doesn't crash the app.
    return {
      push(text) {
        console.warn('[ToastProvider missing]', text);
      },
    };
  }
  return ctx;
}

// Bind the toast API to any pub/sub source so modules can publish a toast
// without importing this hook directly. Wired by AppShell at startup with the
// in-app event bus from @pc/core.
export function bindToastsToBus(
  push: ToastApi['push'],
  on: (
    event: 'app.toast',
    handler: (p: { text: string; tone?: ToastTone }) => void,
  ) => () => void,
): () => void {
  return on('app.toast', (p) => push(p.text, p.tone));
}
